import React, { useEffect } from 'react';
import Button from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { Box, Text, Flex } from '@atlaskit/primitives';
import { RequiredAsterisk } from '@atlaskit/form';
import { ModalHeader } from '@atlaskit/modal-dialog';
import { UpdateBillToAndShipToParties } from '@atlassian/cssxp-types-billing';
import { ShipToPartySidebar } from '@css-xp/ppc-ship-to-party-sidebar';
import { InfoPanel } from '@css-xp/base';
import { getSupportUrl } from '@css-xp/ppc-common';
import CompanyNameInput from './company-name-input';
import { CreateCustomerWrapperContainer } from './create-customer-wrapper.styles';
import { CreateCustomerSuccess } from './create-customer-success';
import { CreateCustomerHeader } from './create-customer-header';

export const CreateCustomerFormStages = {
  COMPANY_NAME: 0,
  ADD_ADDRESS: 1,
  SUCCESS: 2,
};

export type CreateCustomerWrapperProps = {
  shouldAllowMultipleAddresses?: boolean;
  formStages?: string[];
  onClose: () => void;
  onSubmit: (addressess: UpdateBillToAndShipToParties[]) => void;
  transactionAccountId: string;
  showSuccessMessage?: boolean;
  showErrorMessage?: boolean;
  isSubmitting?: boolean;
  currentStage?: number;
  isSoldToAddressArchivalEnabled?: boolean;
};

export const CreateCustomerWrapper = ({
  shouldAllowMultipleAddresses,
  formStages = Object.keys(CreateCustomerFormStages),
  onSubmit,
  transactionAccountId,
  showSuccessMessage,
  showErrorMessage,
  onClose,
  isSubmitting,
  currentStage: currentStageProps,
  isSoldToAddressArchivalEnabled,
}: CreateCustomerWrapperProps) => {
  const [currentStage, setCurrentStage] = React.useState(currentStageProps || CreateCustomerFormStages.COMPANY_NAME);
  const [companyName, setCompanyName] = React.useState('');
  const [addressess, setAddressess] = React.useState([{}]);
  const [address, setAddress] = React.useState({});
  const [isSubmitDisabled, setIsSubmitDisabled] = React.useState(true);

  useEffect(() => {
    if (showSuccessMessage) {
      setCurrentStage(CreateCustomerFormStages.SUCCESS);
    }
  }, [showSuccessMessage]);

  const onPrevious = (isSubmitDisabled = false) => {
    setIsSubmitDisabled(isSubmitDisabled);
    setCurrentStage(CreateCustomerFormStages.COMPANY_NAME);
  };

  const onCompleteSoldToAddressStep = (
    _customerDetails: any,
    updateBillToPartyPayload: any,
    soldToAddresses: UpdateBillToAndShipToParties[] = []
  ) => {
    onSubmit(shouldAllowMultipleAddresses ? soldToAddresses : updateBillToPartyPayload);
  };

  const getFormStep = () => {
    switch (currentStage) {
      case 0:
        return (
          <CompanyNameInput
            transactionAccountId={transactionAccountId}
            companyName={companyName}
            formStages={formStages}
            currentStage={currentStage}
            onNext={(company: string) => {
              setCompanyName(company);
              setCurrentStage(CreateCustomerFormStages.ADD_ADDRESS);
            }}
            isSoldToAddressArchivalEnabled={isSoldToAddressArchivalEnabled}
          />
        );
      case 1:
        return (
          <ShipToPartySidebar
            transactionAccountId={transactionAccountId}
            hideAddFromBillToAddress={true}
            contentString={{
              SUBMIT_BUTTON: 'Create',
              NOTIFICATION_SUCCESS_HEADING: 'Customer added',
            }}
            FormStages={formStages}
            showPricingEligibility={true}
            hideUpdateWarningMessage={true}
            shouldHideNameField={true}
            isProgressIndicatorEnabled={true}
            shouldHideHeader={true}
            shouldAllowMultipleAddresses={shouldAllowMultipleAddresses}
            onCancelClick={onPrevious}
            shouldSubmitBeDisabled={isSubmitDisabled}
            cancelButtonText="Back"
            currentProgressIndex={currentStage}
            onSubmitCustomerStep={onCompleteSoldToAddressStep}
            addressess={addressess}
            setAddressess={setAddressess}
            setAddress={shouldAllowMultipleAddresses ? undefined : setAddress}
            isAddType={true}
            isSubmitting={isSubmitting}
            shipToAddress={{
              ...address,
              name: companyName,
            }}
          />
        );
      case 2:
        return (
          <CreateCustomerSuccess
            onClose={onClose}
            formStages={formStages}
            companyName={companyName}
            transactionAccountId={transactionAccountId}
          />
        );
    }
  };

  return (
    <>
      <ModalHeader>
        <Flex direction="column">
          <Text weight="bold" size="large">
            Create a customer
          </Text>
          <Text size="medium">
            Required fields are marked with an asterisk <RequiredAsterisk />
          </Text>
        </Flex>
        <Button appearance="subtle" onClick={onClose} testId="add-customer-modal-close-button-with-icon">
          <CrossIcon size="small" label="Close Modal" primaryColor={token('color.text.subtle')} />
        </Button>
      </ModalHeader>
      <CreateCustomerWrapperContainer
        isAddressStage={currentStage === CreateCustomerFormStages.ADD_ADDRESS}
        data-testid="create-customer-wrapper"
      >
        <CreateCustomerHeader currentStage={currentStage} />
        <Box
          paddingBlockStart="space.100"
          paddingBlockEnd={currentStage === CreateCustomerFormStages.ADD_ADDRESS ? 'space.0' : 'space.100'}
          paddingInline={currentStage === CreateCustomerFormStages.ADD_ADDRESS ? 'space.300' : 'space.0'}
        >
          {showErrorMessage && (
            <InfoPanel
              type="error"
              message={<p>We couldn't create the customer. Try again or contact {getSupportUrl()}</p>}
            />
          )}
        </Box>
        {getFormStep()}
      </CreateCustomerWrapperContainer>
    </>
  );
};
